export type WorkdayActor = "parent" | "student";

export type ActiveStepEligibility =
  "Eligible" | "Done" | "Deferred" | "Blocked";

export type ActiveStepQueueItem = {
  id: string;
  stepId: string;
  eligibility: ActiveStepEligibility;
  resourceIds: readonly string[];
};

export type ActiveStepQueue = {
  id: string;
  revision: number;
  state: "Draft" | "Ready" | "Active" | "Closed";
  items: readonly ActiveStepQueueItem[];
};

export type StudentWorkState = {
  studentId: string;
  revision: number;
  queue: ActiveStepQueue | null;
  activeStepId: string | null;
  activeQueueItemId: string | null;
  changedBy: WorkdayActor | null;
  changedAt: string;
};

export type StartActiveStepIntent = "start" | "switch";

export type StartActiveStep = {
  expectedRevision: number;
  queueId: string;
  queueItemId: string;
  intent: StartActiveStepIntent;
  actor: "student";
  changedAt: string;
};

export type ActiveStepWinner = {
  stepId: string;
  queueItemId: string;
};

export type StartActiveStepDecision =
  | {
      ok: true;
      kind: "changed";
      previousActiveStepId: string | null;
      state: StudentWorkState;
    }
  | { ok: true; kind: "unchanged"; state: StudentWorkState }
  | {
      ok: false;
      code: "REVISION_CONFLICT";
      currentRevision: number;
      changedBy: WorkdayActor | null;
      changedAt: string;
      authoritativeWinner: ActiveStepWinner | null;
      state: StudentWorkState;
    }
  | {
      ok: false;
      code:
        | "ACTIVE_QUEUE_REQUIRED"
        | "QUEUE_MISMATCH"
        | "QUEUE_ITEM_NOT_FOUND"
        | "QUEUE_ITEM_NOT_ELIGIBLE"
        | "SWITCH_WITHOUT_ACTIVE_STEP"
        | "ACTIVE_SELECTION_INVALID";
      message: string;
    }
  | {
      ok: false;
      code: "SWITCH_REQUIRED";
      message: string;
      activeStepId: string;
      activeQueueItemId: string;
    };

export type InspectQueueItemDecision =
  | {
      ok: true;
      item: ActiveStepQueueItem;
      state: StudentWorkState;
    }
  | {
      ok: false;
      code: "QUEUE_ITEM_NOT_FOUND";
      message: string;
      state: StudentWorkState;
    };

export type ResourceLaunchDecision =
  | { ok: true; resourceId: string }
  | {
      ok: false;
      code: "NO_ACTIVE_STEP" | "RESOURCE_NOT_ACTIVE_STEP";
      message: string;
    };

function authoritativeWinner(state: StudentWorkState): ActiveStepWinner | null {
  return state.activeStepId !== null && state.activeQueueItemId !== null
    ? {
        stepId: state.activeStepId,
        queueItemId: state.activeQueueItemId,
      }
    : null;
}

function startActiveStep(
  state: StudentWorkState,
  command: StartActiveStep,
): StartActiveStepDecision {
  if (command.expectedRevision !== state.revision) {
    return {
      ok: false,
      code: "REVISION_CONFLICT",
      currentRevision: state.revision,
      changedBy: state.changedBy,
      changedAt: state.changedAt,
      authoritativeWinner: authoritativeWinner(state),
      state,
    };
  }

  const queue = state.queue;
  if (queue === null || queue.state !== "Active") {
    return {
      ok: false,
      code: "ACTIVE_QUEUE_REQUIRED",
      message: "A Step can start only from the Active Daily Queue.",
    };
  }
  if (queue.id !== command.queueId) {
    return {
      ok: false,
      code: "QUEUE_MISMATCH",
      message: "Choose a Step in the current Active Daily Queue.",
    };
  }

  const item = queue.items.find(({ id }) => id === command.queueItemId);
  if (item === undefined) {
    return {
      ok: false,
      code: "QUEUE_ITEM_NOT_FOUND",
      message: "Choose a Step in the Active Daily Queue.",
    };
  }
  if (item.eligibility !== "Eligible") {
    return {
      ok: false,
      code: "QUEUE_ITEM_NOT_ELIGIBLE",
      message: "This Step is not eligible to start.",
    };
  }

  const hasActiveStep = state.activeStepId !== null;
  const hasActiveQueueItem = state.activeQueueItemId !== null;
  if (hasActiveStep !== hasActiveQueueItem) {
    return {
      ok: false,
      code: "ACTIVE_SELECTION_INVALID",
      message: "Refresh the Active Step before continuing.",
    };
  }

  if (
    state.activeStepId === item.stepId &&
    state.activeQueueItemId === item.id
  ) {
    return { ok: true, kind: "unchanged", state };
  }

  if (hasActiveStep && hasActiveQueueItem && command.intent !== "switch") {
    return {
      ok: false,
      code: "SWITCH_REQUIRED",
      message: "Use Switch to replace the current Active Step.",
      activeStepId: state.activeStepId as string,
      activeQueueItemId: state.activeQueueItemId as string,
    };
  }
  if (!hasActiveStep && command.intent === "switch") {
    return {
      ok: false,
      code: "SWITCH_WITHOUT_ACTIVE_STEP",
      message: "Use Start when there is no current Active Step.",
    };
  }

  return {
    ok: true,
    kind: "changed",
    previousActiveStepId: state.activeStepId,
    state: {
      ...state,
      revision: state.revision + 1,
      activeStepId: item.stepId,
      activeQueueItemId: item.id,
      changedBy: command.actor,
      changedAt: command.changedAt,
    },
  };
}

function inspectQueueItem(
  state: StudentWorkState,
  queueItemId: string,
): InspectQueueItemDecision {
  const item = state.queue?.items.find(({ id }) => id === queueItemId);
  if (item === undefined) {
    return {
      ok: false,
      code: "QUEUE_ITEM_NOT_FOUND",
      message: "Choose a Step in the current Daily Queue.",
      state,
    };
  }
  return { ok: true, item, state };
}

function authorizeActiveStepResourceLaunch(
  state: StudentWorkState,
  resourceId: string,
): ResourceLaunchDecision {
  if (state.activeStepId === null || state.activeQueueItemId === null) {
    return {
      ok: false,
      code: "NO_ACTIVE_STEP",
      message: "Start a Step before launching its Resource.",
    };
  }

  const activeItem = state.queue?.items.find(
    ({ id, stepId }) =>
      id === state.activeQueueItemId && stepId === state.activeStepId,
  );
  if (activeItem?.resourceIds.includes(resourceId) !== true) {
    return {
      ok: false,
      code: "RESOURCE_NOT_ACTIVE_STEP",
      message: "Launch a Resource belonging to the Active Step.",
    };
  }
  return { ok: true, resourceId };
}

export const activeStepWorkday = {
  startActiveStep,
  inspectQueueItem,
  authorizeActiveStepResourceLaunch,
};
